<script>
  // Этап 25.1: строка состояния «мозга» — провайдеры LLM не отвечают. Стоит в
  // потоке под шапкой (а не оверлеем, как баннер соединения): сбой может висеть
  // часами, и строка не должна закрывать кнопки шапки. Тап — шторка: всем
  // объяснение, владельцу сервера backend отдаёт ещё и провайдеров. Этап 25.6:
  // владельцу всегда видна тонкая строка моделей (OpenAI, Z.AI, Yandex, Home:
  // зелёная точка — доступна, красная — нет); лёгший выход пула (25.4) — в ней же.
  // Этап 33: на строке — чип «новые: N» (новые модели у провайдеров), в шторке —
  // модели цепочек, которых у провайдера нет, и вход в «Модели провайдеров».
  import { api } from '../api.js';
  import { brainStatusStore } from '../stores.js';
  import { TEXT } from '../ui-text.js';
  import { formatMoment, modelLabel } from '../model-picker.js';
  import BottomSheet from './BottomSheet.svelte';
  import ProviderModelsSheet from './ProviderModelsSheet.svelte';

  let open = false;
  let catalogOpen = false;
  let loading = false;
  let loadError = '';
  let details = null;

  $: level = $brainStatusStore.level;
  $: brainTrouble = level === 'degraded' || level === 'down';
  $: exitsTrouble = ($brainStatusStore.exitsDown || 0) > 0;
  $: strip = Object.entries($brainStatusStore.providerStates || {});
  $: sheetLevel = details?.level || level;
  $: explainKey = sheetLevel === 'ok' && (details?.exitsDown || $brainStatusStore.exitsDown) ? 'exits' : sheetLevel;
  $: newModels = $brainStatusStore.newModels || 0;

  function providerName(provider) {
    return TEXT.brain.providerNames[provider] || provider;
  }

  function exitName(name) {
    return TEXT.brain.exitNames[name] || name;
  }

  function openCatalog() {
    open = false;
    catalogOpen = true;
  }

  function roleLabels(roles) {
    return [...new Set((roles || []).map((role) => TEXT.modelCatalog.roles[role] || role))].join(', ');
  }

  async function openDetails() {
    open = true;
    loading = true;
    loadError = '';
    details = null;
    try {
      details = await api.getBrainStatus();
    } catch (error) {
      loadError = TEXT.brain.loadFailed;
    } finally {
      loading = false;
    }
  }
</script>

<div role="status" aria-live="polite">
  {#if brainTrouble}
    <button
      type="button"
      class="brain-line"
      class:down={level === 'down'}
      aria-haspopup="dialog"
      on:click={openDetails}
    >
      <span class="icon" aria-hidden="true">⚠</span>
      <span class="text">{TEXT.brain.line[level]}</span>
      {#if $brainStatusStore.since}
        <span class="since">{TEXT.brain.since} {formatMoment($brainStatusStore.since)}</span>
      {/if}
    </button>
  {/if}
  {#if strip.length}
    <div class="strip-row">
      <button
        type="button"
        class="model-strip"
        aria-haspopup="dialog"
        aria-label={`${TEXT.brain.stripLabel}: ${strip.map(([p, s]) => `${providerName(p)} — ${TEXT.brain.stripStates[s] || s}`).join(', ')}${exitsTrouble ? `. ${TEXT.brain.line.exits}` : ''}`}
        on:click={openDetails}
      >
        {#each strip as [provider, state] (provider)}
          <span class="model" data-state={state}>
            <span class="dot" aria-hidden="true"></span>{providerName(provider)}
          </span>
        {/each}
        {#if exitsTrouble}
          <span class="exits">{TEXT.brain.line.exits}</span>
        {/if}
      </button>
      {#if newModels > 0}
        <button
          type="button"
          class="new-chip"
          aria-haspopup="dialog"
          aria-label={`${TEXT.brain.newModelsLabel}: ${newModels}`}
          on:click={openCatalog}
        >
          {TEXT.brain.newModels} {newModels}
        </button>
      {/if}
    </div>
  {/if}
</div>

<BottomSheet {open} title={TEXT.brain.title} on:close={() => (open = false)}>
  <div class="brain-sheet">
    <p class="explain">{TEXT.brain.explain[explainKey] || TEXT.brain.explain.ok}</p>
    {#if loading}
      <p class="muted">{TEXT.brain.loading}</p>
    {:else if loadError}
      <p class="muted" role="alert">{loadError}</p>
    {:else if details?.providers?.length}
      <h3>{TEXT.brain.providersTitle}</h3>
      <ul class="providers">
        {#each details.providers as item (item.provider)}
          <li class="provider" data-state={item.state}>
            <div class="provider-head">
              <span class="dot" aria-hidden="true"></span>
              <span class="name">{providerName(item.provider)}</span>
              {#if item.primary}<span class="tag">{TEXT.brain.primary}</span>{/if}
              <span class="state">{TEXT.brain.states[item.state] || item.state}</span>
            </div>
            <div class="provider-meta">
              {#if item.since}<span>{TEXT.brain.since} {formatMoment(item.since)}</span>{/if}
              {#if item.state === 'down' && item.lastError}
                <span>{TEXT.brain.lastError}: {TEXT.brain.errors[item.lastError] || item.lastError}</span>
              {/if}
              {#if item.lastOkAt}<span>{TEXT.brain.lastOk}: {formatMoment(item.lastOkAt)}</span>{/if}
            </div>
          </li>
        {/each}
      </ul>
      {#if details.exits?.length}
        <h3>{TEXT.brain.exitsTitle}</h3>
        <ul class="providers">
          {#each details.exits as item (item.exit)}
            <li class="provider" data-state={item.state}>
              <div class="provider-head">
                <span class="name">{exitName(item.exit)}</span>
                <span class="state">{TEXT.brain.states[item.state] || item.state}</span>
              </div>
              <div class="provider-meta">
                {#each Object.entries(item.providers || {}) as [provider, state] (provider)}
                  <span>{providerName(provider)}: {TEXT.brain.states[state] || state}</span>
                {/each}
                {#if item.state === 'down' && item.lastError}
                  <span>{TEXT.brain.lastError}: {TEXT.brain.errors[item.lastError] || item.lastError}</span>
                {/if}
                {#if item.since}<span>{TEXT.brain.since} {formatMoment(item.since)}</span>{/if}
              </div>
            </li>
          {/each}
        </ul>
      {/if}
      {#if details.modelWarnings?.length}
        <h3>{TEXT.brain.warningsTitle}</h3>
        <ul class="providers">
          {#each details.modelWarnings as warning (warning.model)}
            <li class="provider" data-state="down">
              <div class="provider-head">
                <span class="name">{modelLabel(warning.model)}</span>
                <span class="state">{TEXT.brain.warningReasons[warning.reason] || warning.reason}</span>
              </div>
              {#if warning.roles?.length}
                <div class="provider-meta"><span>{roleLabels(warning.roles)}</span></div>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
      <button type="button" class="catalog-link" aria-haspopup="dialog" on:click={openCatalog}>
        <span>{TEXT.brain.catalogLink}</span>
        {#if newModels > 0}<span class="tag">{TEXT.brain.newModels} {newModels}</span>{/if}
        <span class="chevron" aria-hidden="true">›</span>
      </button>
    {/if}
    <button type="button" class="close" on:click={() => (open = false)}>{TEXT.brain.close}</button>
  </div>
</BottomSheet>

<ProviderModelsSheet open={catalogOpen} on:close={() => (catalogOpen = false)} />

<style>
  .brain-line {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.25rem 0.5rem;
    width: 100%;
    padding: 0.5rem 1rem;
    border: none;
    border-bottom: 1px solid var(--border-color, transparent);
    background: var(--warning-bg);
    color: var(--warning-text);
    font: inherit;
    font-size: 0.875rem;
    text-align: center;
    cursor: pointer;
  }

  /* Строка моделей владельца (этап 25.6): тонкая, в потоке под шапкой. */
  .strip-row {
    display: flex;
    align-items: center;
    border-bottom: 1px solid var(--border-color, transparent);
    background: var(--bg-secondary);
  }

  .model-strip {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.125rem 0.75rem;
    min-width: 0;
    padding: 0.1875rem 0.75rem;
    border: none;
    background: none;
    color: var(--text-secondary);
    font: inherit;
    font-size: 0.75rem;
    line-height: 1.3;
    cursor: pointer;
  }

  /* Этап 33: чип «новые: N» — маленький, но с зоной касания 44px. */
  .new-chip {
    position: relative;
    flex: none;
    margin-right: 0.5rem;
    padding: 0 0.5rem;
    border: none;
    border-radius: 999px;
    background: var(--warning-bg);
    color: var(--warning-text);
    font: inherit;
    font-size: 0.75rem;
    line-height: 1.3rem;
    white-space: nowrap;
    cursor: pointer;
  }

  .new-chip::after {
    content: '';
    position: absolute;
    inset: -0.75rem -0.375rem;
  }

  .catalog-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-height: 44px;
    padding: 0.5rem 0.75rem;
    border: none;
    border-radius: 10px;
    background: var(--bg-secondary);
    color: var(--text-primary);
    font: inherit;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
  }

  .chevron {
    margin-left: auto;
    color: var(--text-secondary);
  }

  .model {
    display: inline-flex;
    align-items: center;
    gap: 0.3125rem;
    white-space: nowrap;
  }

  .dot {
    width: 0.5rem;
    height: 0.5rem;
    flex: none;
    border-radius: 50%;
    background: var(--text-tertiary, var(--text-secondary));
    opacity: 0.6;
  }

  [data-state='ok'] > .dot,
  .provider[data-state='ok'] .dot {
    background: var(--success-color, #2e9d5b);
    opacity: 1;
  }

  [data-state='down'] > .dot,
  .provider[data-state='down'] .dot {
    background: var(--error-text);
    opacity: 1;
  }

  .exits {
    color: var(--warning-text);
    white-space: nowrap;
  }

  .brain-line.down {
    background: var(--error-bg);
    color: var(--error-text);
  }

  .since {
    opacity: 0.8;
  }

  .brain-sheet {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .explain {
    margin: 0;
    line-height: 1.4;
  }

  .muted {
    margin: 0;
    color: var(--text-secondary);
  }

  h3 {
    margin: 0.25rem 0 0;
    font-size: 0.875rem;
    color: var(--text-secondary);
    font-weight: 600;
  }

  .providers {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .provider {
    padding: 0.5rem 0.75rem;
    border-radius: 10px;
    background: var(--bg-secondary);
  }

  .provider-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .name {
    font-weight: 600;
  }

  .tag {
    font-size: 0.75rem;
    padding: 0 0.375rem;
    border-radius: 6px;
    background: var(--bg-tertiary, var(--bg-primary));
    color: var(--text-secondary);
  }

  .state {
    margin-left: auto;
    font-size: 0.875rem;
  }

  .provider[data-state='down'] .state {
    color: var(--error-text);
  }

  .provider-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 0.75rem;
    margin-top: 0.25rem;
    font-size: 0.8125rem;
    color: var(--text-secondary);
  }

  .close {
    align-self: stretch;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    background: var(--bg-tertiary);
    color: var(--text-primary);
    font: inherit;
    font-weight: 500;
    cursor: pointer;
  }
</style>
